import React from 'react';

interface PollingStatusBarProps {
  isPolling: boolean;
  lastPollTime: Date | null;
  error: Error | null;
  isSubscribed: boolean;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    borderRadius: '6px',
    padding: '0.5rem 0.75rem',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    marginBottom: '0.75rem',
  },
  errorBar: {
    backgroundColor: '#fff3e0',
    border: '1px solid #ffb74d',
    color: '#e65100',
  },
  liveBar: {
    backgroundColor: '#e8f5e9',
    border: '1px solid #a5d6a7',
    color: '#2e7d32',
  },
  pollingBar: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #90caf9',
    color: '#1565c0',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '0.375rem',
  },
  greenDot: {
    backgroundColor: '#4caf50',
  },
  orangeDot: {
    backgroundColor: '#ff9800',
  },
  blueDot: {
    backgroundColor: '#1976d2',
  },
  statusLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  timeText: {
    color: '#999',
    fontSize: '0.7rem',
  },
};

function formatLastPoll(date: Date | null): string {
  if (!date) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function PollingStatusBar({ isPolling, lastPollTime, error, isSubscribed }: PollingStatusBarProps) {
  // Error state — show warning banner
  if (error) {
    return (
      <div style={{ ...styles.container, ...styles.errorBar }} role="alert">
        <div style={styles.statusLeft}>
          <span style={{ ...styles.dot, ...styles.orangeDot }} />
          <span>Connection issue: {error.message}</span>
        </div>
        {lastPollTime && (
          <span style={styles.timeText}>Last update: {formatLastPoll(lastPollTime)}</span>
        )}
      </div>
    );
  }

  // Subscribed and actively polling
  if (isSubscribed && isPolling) {
    return (
      <div style={{ ...styles.container, ...styles.pollingBar }}>
        <div style={styles.statusLeft}>
          <span style={{ ...styles.dot, ...styles.blueDot }} />
          <span>Live updates: polling mode</span>
        </div>
        {lastPollTime && (
          <span style={styles.timeText}>Last poll: {formatLastPoll(lastPollTime)}</span>
        )}
      </div>
    );
  }

  // Connected and subscribed — show subtle live indicator
  if (isSubscribed) {
    return (
      <div style={{ ...styles.container, ...styles.liveBar }}>
        <div style={styles.statusLeft}>
          <span style={{ ...styles.dot, ...styles.greenDot }} />
          <span>Live</span>
        </div>
        {lastPollTime && (
          <span style={styles.timeText}>Last poll: {formatLastPoll(lastPollTime)}</span>
        )}
      </div>
    );
  }

  // Not subscribed — don't show anything
  return null;
}
